/* FieldVisibilityWizard — controls which canonical catalog fields are
 * visible to buyers. Left pane = toggles; right pane = live buyer-view
 * preview of a sample product so the seller sees the effect of each
 * toggle in real time.
 *
 * The sample product comes from the seller's own catalog when possible
 * (we fetch the freshest product so the preview feels real). If the
 * catalog is empty we render a placeholder demo product.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Eye, EyeOff, Settings2, Save } from "lucide-react";
import BuyerViewPreview from "./BuyerViewPreview";

const FIELD_LABELS = {
  sku:          { l: "SKU code",            d: "Internal product code" },
  name:         { l: "Product name",        d: "Required — always visible" },
  brand:        { l: "Brand",               d: "Manufacturer / vendor name" },
  category:     { l: "Category",            d: "Filter buyers can browse by" },
  description:  { l: "Description",         d: "Long-form product details" },
  price:        { l: "Wholesale price",     d: "Required — always visible" },
  retail_price: { l: "Retail (MSRP)",       d: "Shows margin to the buyer" },
  moq:          { l: "MOQ",                 d: "Minimum order quantity" },
  stock:        { l: "Stock on hand",       d: "Live inventory count" },
  case_size:    { l: "Case size",           d: "Units per case (for Unit/Case toggle)" },
  upc:          { l: "UPC / barcode",       d: "Most sellers hide this from buyers" },
  image:        { l: "Image",               d: "Product photo" },
  fulfillment:  { l: "Fulfillment",         d: "Wholesale / Dropship / Both" },
  badge:        { l: "Badges",              d: "New arrival, top seller, weekly special, …" },
  map_price:    { l: "MAP price",           d: "Minimum advertised price" },
  tags:         { l: "Tags / keywords",     d: "Search keywords" },
};

const FORCED_VISIBLE = new Set(["name", "price"]);

export default function FieldVisibilityWizard({ onClose, sampleProduct }) {
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState(sampleProduct || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, p] = await Promise.all([
          api.get("/seller/catalog-import/visibility"),
          // If parent didn't pass a sample, grab the freshest from the catalog
          sampleProduct ? Promise.resolve({ data: { products: [sampleProduct] } })
                        : api.get("/products?limit=1&sort=newest").catch(() => ({ data: { products: [] } })),
        ]);
        if (cancelled) return;
        setFields(v.data.fields || {});
        const arr = p.data?.products || p.data || [];
        if (Array.isArray(arr) && arr[0]) setSample(arr[0]);
      } catch (e) {
        if (!cancelled) toast.error("Could not load visibility settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sampleProduct]);

  const toggle = (k) => {
    if (FORCED_VISIBLE.has(k)) return;
    setFields(f => ({ ...f, [k]: !f[k] }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...fields };
      FORCED_VISIBLE.forEach(k => { clean[k] = true; });
      await api.put("/seller/catalog-import/visibility", { fields: clean });
      toast.success("Visibility settings saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="visibility-wizard">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-foreground/55"/>Field visibility
          </DialogTitle>
          <DialogDescription>
            Pick which catalog fields your buyers can see. The preview on the right updates as you toggle.
          </DialogDescription>
        </DialogHeader>

        <div className="grid lg:grid-cols-[1fr_320px] gap-0 flex-1 overflow-hidden">
          {/* TOGGLES */}
          <div className="overflow-y-auto px-6 pb-3 border-r border-border" data-testid="visibility-toggles">
            {loading ? (
              <div className="py-10 text-center text-foreground/45">Loading…</div>
            ) : (
              <div className="space-y-1.5 pt-2">
                {Object.entries(FIELD_LABELS).map(([k, meta]) => {
                  const on = !!fields[k] || FORCED_VISIBLE.has(k);
                  const forced = FORCED_VISIBLE.has(k);
                  return (
                    <button key={k} onClick={() => toggle(k)} disabled={forced}
                      data-testid={`vis-${k}`}
                      className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${forced ? "border-border bg-secondary/40 cursor-default" : "border-border bg-white hover:border-foreground/40"}`}>
                      <span className={`shrink-0 w-9 h-5 rounded-full p-0.5 flex items-center transition-colors ${on ? "bg-foreground" : "bg-secondary border border-border"}`}>
                        <span className="w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: on ? "translateX(14px)" : "translateX(0)" }}/>
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium inline-flex items-center gap-2">
                          {meta.l}
                          {forced && <span className="chip bg-foreground/10 text-foreground/55 text-[9px]">Required</span>}
                        </div>
                        <div className="text-[11px] text-foreground/55 mt-0.5">{meta.d}</div>
                      </div>
                      <span className="text-foreground/45 shrink-0">{on ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* LIVE PREVIEW */}
          <div className="overflow-y-auto bg-secondary/30 px-5 pb-5 pt-3" data-testid="visibility-preview-pane">
            <div className="sticky top-0 bg-secondary/30 backdrop-blur z-10 py-2 mb-2">
              <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Buyer view preview</div>
              <div className="text-[10.5px] text-foreground/55 mt-0.5">How a buyer will see this product</div>
            </div>
            <BuyerViewPreview product={sample} visibility={fields}/>
          </div>
        </div>

        <div className="border-t border-border bg-white px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="vis-save"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            <Save className="w-3.5 h-3.5 mr-1.5"/>
            {busy ? "Saving…" : "Save visibility"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
